import React, { useState, useEffect } from 'react'
import { useFragment, graphql } from 'react-relay/hooks'
import { useAuth, useApi } from 'hooks'
import { Button, Modal, TextInput, Text, FlexRow, FlexColumn, Message, Icon } from 'elements'
import ModalContent from 'components/app/ModalContent'
import ModalCloseButton from 'components/app/ModalCloseButton'
import useCompanyCreate from '../hooks/useCompanyCreate'
import useCompanyUpdate from '../hooks/useCompanyUpdate'
import ChoiceButton from 'components/ui/input/ChoiceButton'
import FirstClientIntegrationChoices from '../../report/create/FirstClientIntegrationChoices'

const INTEGRATION_KINDS = ['intuit', 'xero']

export default function CreateClientStep({
  integrationResult,
  company,
  selected,
  onSelect,
  onNext,
  onClose,
}) {
  const [submitting, setSubmitting] = useState(false)
  const [companyName, setCompanyName] = useState('')
  const [createCompany] = useCompanyCreate()
  const [updateCompany] = useCompanyUpdate()
  const { currentOrg, setCurrentCompany } = useAuth()
  const api = useApi()

  const data = useFragment(
    graphql`
      fragment CreateClientStep_company on Company {
        rowId
        id
        name
        integrations(first: 100) {
          nodes {
            status
          }
        }
      }
    `,
    company,
  )

  const integration = data?.integrations?.nodes?.[0]

  const handleNext = async () => {
    setSubmitting(true)

    // data is defined when company has already been created after returning from integration
    if (data) {
      if (data.name !== companyName) {
        await updateCompany({ id: data.id, patch: { name: companyName } })
      }

      if (selected === 'upload' || integration?.status === 'ENABLED') {
        onNext()
        return
      }

      // connect url when returning from failed connection
      const connectUrl = api.integration.getConnectUrl(data.rowId, selected.kind, {
        modal: 'first-client',
        modalBehavior: 'full',
        orgSlug: currentOrg.slug,
        companyId: data.id,
      })

      window.location.href = connectUrl

      return
    }

    // first time on create client modal
    const { company } = await createCompany({
      name: companyName,
      organizationId: currentOrg.id,
    })

    setCurrentCompany(company)

    if (selected === 'upload') {
      onNext()
      return
    }

    const connectUrl = api.integration.getConnectUrl(company.rowId, selected.kind, {
      modal: 'first-client',
      modalBehavior: 'full',
      orgSlug: currentOrg.slug,
      companyId: company.id,
    })

    window.location.href = connectUrl
  }

  const canContinue = companyName.length

  const integrationKind = INTEGRATION_KINDS.includes(selected.kind)

  // data defined after connecting
  const displayRedirectMessage = integrationKind && !data

  // set company field to the current company name when returning from integration
  useEffect(() => {
    if (data) {
      setCompanyName(data.name)
    }
  }, [data])

  return (
    <>
      <ModalContent pt="gigantic" px="gigantic">
        <ModalCloseButton onClose={onClose} />
        <FlexRow justifyContent="center">
          <Text fontSize="24px" fontWeight="bold" mb="large">
            Welcome to Scrutinize
          </Text>
        </FlexRow>
        <FlexRow justifyContent="center">
          <Text fontSize="small" color="neutral.0" mb="xxxlarge">
            Let's get everything set up to run your first report.
          </Text>
        </FlexRow>
        <Text mb="xlarge">Step 1: Add a client</Text>
        <FlexRow mb="xxxlarge">
          <TextInput
            value={companyName}
            autoFocus
            onChange={(event) => setCompanyName(event.target.value)}
            placeholder="Client Name"
          />
        </FlexRow>
        <Text mb="xlarge">Step 2: Add a data source</Text>
        <FlexColumn mb="large">
          {integrationResult?.status === 'success' ? (
            <Message kind="success" mb="xlarge">
              Your account was connected successfully!
            </Message>
          ) : null}
          {integrationResult?.status === 'error' ? (
            <Message kind="danger" mb="xlarge">
              Your account could not be connected.
            </Message>
          ) : null}
          <FlexRow mb="large">
            <FirstClientIntegrationChoices
              company={company}
              selected={selected}
              onSelect={onSelect}
            />
          </FlexRow>
          <FlexRow>
            <ChoiceButton
              icon="document-add"
              selected={selected === 'upload'}
              onPress={() => onSelect('upload')}
              area="small"
            >
              Upload Files
            </ChoiceButton>
          </FlexRow>
        </FlexColumn>
        {displayRedirectMessage && (
          <FlexRow mb="xxxlarge" color="secondary.0" alignItems="center">
            <Icon name="exclamation-circle" mr="medium" />
            <Text fontSize="small" color="inherit">
              You will be redirected to log in and complete the integration setup
            </Text>
          </FlexRow>
        )}
      </ModalContent>
      <Modal.Footer
        display="flex"
        alignItems="center"
        justifyContent="flex-end"
        width="100%"
        px="gigantic"
        pb="gigantic"
      >
        <Button kind="primary" onClick={handleNext} disabled={submitting || !canContinue}>
          Next
        </Button>
      </Modal.Footer>
    </>
  )
}
